
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Card,
  Chip,
  IconButton,
  Paper,
} from "@mui/material";
import {
  Search,
  Add,
} from "@mui/icons-material";
import { EditOutlined } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { getAllSeasonsAction } from "../../store/actions";


const gridColsDesktop = '1.8fr 1fr 1fr 0.8fr';

export default function Seasons() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { successData: seasonsData } = useSelector((state) => state.getAllSeasonsReducer);
  const seasonList = seasonsData?.data?.seasons || [];
  const [search, setSearch] = useState("");
  const [seasonFilter, setSeasonFilter] = useState("ALL");
  const [yearFilter, setYearFilter] = useState("ALL");

  const seasonNameOptions = useMemo(() => {
    const names = Array.from(new Set((seasonList || []).map(s => s.name).filter(Boolean)));
    return ["ALL", ...names];
  }, [seasonList]);

  const yearOptions = useMemo(() => {
    const years = Array.from(new Set((seasonList || []).map(s => s.year).filter(Boolean)));
    return ["ALL", ...years];
  }, [seasonList]);

  const filteredSeasons = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (seasonList || []).filter(s => {
      const matchesName = q ? (s.name || "").toLowerCase().includes(q) : true;
      const matchesSeason = seasonFilter === "ALL" ? true : s.name === seasonFilter;
      const matchesYear = yearFilter === "ALL" ? true : s.year === yearFilter;
      return matchesName && matchesSeason && matchesYear;
    });
  }, [seasonList, search, seasonFilter, yearFilter]);

  useEffect(() => {
    dispatch(getAllSeasonsAction());
  }, [dispatch]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
        mt: { xs: 0, sm: 0, md: 0 },
      }}
    >
      {/* Header */}
      <Box
        display={{ xs: "block", sm: "flex" }}
        justifyContent="space-between"
        mb={{ xs: 1, sm: 2, md: 2 }}
        sx={{ gap: { xs: 2, sm: 2, md: 3, lg: 4 } }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, mt: { sm: 7, lg: 0, xs: 0 } }}>
          Seasons Management
        </Typography>


      </Box>

      {/* Filters */}
      <Card
        sx={{
          p: { xs: 2, sm: 2, md: 2, lg: 2 },
          mb: { xs: 3, sm: 4 },
          borderRadius: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 2, md: 2 },
          alignItems: { xs: "stretch", sm: "center" },
        }}
      >

        <TextField
          placeholder="Search ..."
          size="small"
          slotProps={{ input: { startAdornment: <Search sx={{ mr: 1 }} /> } }}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 3 },
            width: { xs: "100%", sm: 180, md: 260, lg: 320 }
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Show selects in a row for mobile */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'row' },
          gap: { xs: 1, sm: 2 },
          width: { xs: '100%', sm: 'auto' },
        }}>
          <Select
            size="small"
            value={seasonFilter}
            onChange={(e) => setSeasonFilter(e.target.value)}
            sx={{ width: { xs: "100%", sm: 120, md: 160, lg: 180 }, borderRadius: 3 }}
          >
            {seasonNameOptions.map(opt => (
              <MenuItem key={opt} value={opt}>{opt === "ALL" ? "All Seasons" : opt}</MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            sx={{ width: { xs: "100%", sm: 100, md: 140, lg: 160 }, borderRadius: 3 }}
          >
            {yearOptions.map(opt => (
              <MenuItem key={opt} value={opt}>{opt === "ALL" ? "All Years" : opt}</MenuItem>
            ))}
          </Select>
        </Box>

        <Box flexGrow={1} sx={{ display: { xs: "none", sm: "block" } }} />

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/masterdata/seasonscreate')}
          sx={{
            background: "#2563EB",
            textTransform: "none",
            borderRadius: 2,
            width: { xs: "100%", sm: "auto", md: "auto", lg: 200, xl: 220 },
            mt: { xs: 1, sm: 0 },
            alignSelf: { lg: "center", xl: "center" },
            fontSize: { xs: 14, sm: 14, md: 15, lg: 12 },
            px: { xs: 3, sm: 2, md: 3, lg: 1 },
          }}
        >
          Add New Season
        </Button>
      </Card>

      {/* Table Header */}
      <Box
        display={{ xs: 'none', sm: 'grid' }}
        gridTemplateColumns={{ sm: gridColsDesktop }}
        alignItems="center"
        px={2}
        py={2}
        sx={{ columnGap: 0 }}
        mb={{ sm: 1, md: 2 }}
      >
        {['Season Name', 'Year', 'Status', 'Actions'].map((h) => (
          <Typography
            key={h}
            variant="caption"
            fontWeight={600}
            sx={{ textTransform: 'uppercase', textAlign: h === 'Actions' ? 'right' : 'left' }}
          >
            {h}
          </Typography>
        ))}
      </Box>

      {/* Rows */}
      {filteredSeasons.map((season) => (
        <Paper
          key={season.seasonId}
          elevation={0}
          sx={{
            mb: { xs: 2, sm: 2, md: 2.5 },
            p: { xs: 2, sm: 2, md: 2, lg: 1 },
            borderRadius: '12px',
            borderTopRightRadius: '12px',
            borderBottomRightRadius: '12px',
            display: {
              xs: "flex",
              sm: "grid"
            },
            flexDirection: { xs: "column", sm: "unset" },
            gap: { xs: 0, sm: 2, md: 2, lg: 2, xl: 2 },
            columnGap: 0,
            gridTemplateColumns: { sm: gridColsDesktop },
            alignItems: "center",
            border: "1px solid #E5E7EB",
            backgroundColor: '#FFFFFF',
            overflow: 'hidden',
            // width: { xs: '90%', sm: '100%', md: '90%', lg: '98.8%', },
            '&:hover': {
              border: '2px solid #2563EB',
              boxShadow: '0 4px 16px 0 rgba(37,99,235,0.15)',
            },
            cursor: 'pointer',
          }}
        >
          {/* Mobile layout: show labels beside values */}
          <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", width: "100%", gap: 1 }}>
            <Box display="flex" alignItems="center" gap={1} sx={{ flexWrap: 'nowrap' }}>
              <Typography fontSize={15} color="#000" minWidth={90}>SEASON:</Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2">{season.name}</Typography>
              </Box>
              <Chip
                label={season.isCurrent ? 'Active' : 'Inactive'}
                size="small"
                sx={{
                  ml: 'auto',
                  height: 20,
                  fontSize: 11,
                  px: 0.5,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  ...(season.isCurrent
                    ? { backgroundColor: '#D1FAE5', color: '#047857' }
                    : { backgroundColor: '#FEE2E2', color: '#B91C1C' }),
                }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontSize={15} color="#000" minWidth={90}>YEAR:</Typography>
              <Typography variant="body2">{season.year || '-'}</Typography>
            </Box>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
              <Box sx={{ display: "flex", gap: 1 }}>
               <IconButton onClick={() => navigate('/masterdata/seasonscreate', { state: { seasonId: season.seasonId, mode: 'edit' } })}>
                  <EditOutlined fontSize="small" sx={{ color: "#2563EB", filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Desktop/tablet layout: original grid */}
          <Box display="flex" gap={{ xs: 2, sm: 2, md: 3 }} alignItems="center" sx={{ width: { xs: "100%", sm: "auto" }, display: { xs: "none", sm: "flex" } }}>
            <Typography variant="body2">{season.name}</Typography>
          </Box>
          <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" }, fontSize: { sm: 14, md: 15, lg: 16 } }}>{season.year || '-'} </Typography>
          <Chip
            label={season.isCurrent ? 'Active' : 'Inactive'}
            sx={{
              width: { sm: 80, md: 90, lg: 100 },
              ...(season.isCurrent
                ? { backgroundColor: '#D1FAE5', color: '#047857' }
                : { backgroundColor: '#FEE2E2', color: '#B91C1C' }),
              display: { xs: "none", sm: "inline-flex" },
              fontSize: { sm: 12, md: 13, lg: 14 },
            }}
            size="small"
          />
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
            <IconButton onClick={() => navigate('/masterdata/seasonscreate', { state: { seasonId: season.seasonId, mode: 'edit' } })}>
              <EditOutlined fontSize="small" sx={{ color: "#2563EB", filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
            </IconButton>

          </Box>
        </Paper>
      ))}
    </Box>
  );
}

